import React, { useState, useEffect, useCallback } from 'react';
import { SimpleButton, LoadingButton, Input, DropdownMenu, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useHttp } from '@veda/dashboard-ui';
import { useAuthUser } from '../hooks/useAuthUser';

const TIPO_LABELS = {
  nomina_rup:                      'Nomina RUP (art. 15 D.lgs 36/2023)',
  delibera:                        'Delibera',
  determinazione:                  'Determinazione',
  provvedimento:                   'Provvedimento',
  decreto:                         'Decreto',
  ordinanza:                       'Ordinanza',
  comunicazione_avvio:             'Comunicazione avvio (L. 241/1990)',
  dichiarazione_interesse_pubblico:'Dichiarazione interesse pubblico',
  approvazione_prog_triennale:     'Delibera approvazione Programma Triennale',
  approvazione_dip:                'Delibera/Determinazione approvazione DIP',
  selezione_proposte:              'Atto di selezione proposta (valutazione comparativa)',
  richiesta_integrazioni_documentali: 'Richiesta formale integrazioni documentali',
};

const STATO_COLORS = {
  bozza:      'bg-gray-100 text-gray-700',
  in_firma:   'bg-yellow-100 text-yellow-700',
  approvato:  'bg-green-100 text-green-700',
  pubblicato: 'bg-blue-100 text-blue-700',
};

function StatoAtto({ stato }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATO_COLORS[stato] ?? 'bg-gray-100 text-gray-600'}`}>
      {stato}
    </span>
  );
}

function FormComunicazioneAvvio({ procedimentoId, onSuccess, _locate }) {
  const api = usePppApi();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ufficioResponsabile: '',
    termineConclusioneGiorni: '90',
    modalitaAccessoAtti: '',
    sostitutivaNome: '',
    sostitutivaContatto: '',
  });

  const handleSubmit = () => {
    setSaving(true);
    api.generaComunicazioneAvvio(procedimentoId, form, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onFormError: () => setSaving(false),
    });
  };

  const valid = form.ufficioResponsabile && form.termineConclusioneGiorni && form.modalitaAccessoAtti;

  return (
    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50 space-y-3">
      <p className="text-sm font-medium text-blue-800">{_locate('ppp_atti_comunicazione_avvio_titolo')}</p>
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={_locate('ppp_atti_ufficio_responsabile')}
          required
          value={form.ufficioResponsabile}
          onChange={(e) => setForm((f) => ({ ...f, ufficioResponsabile: e.target.value }))}
        />
        <Input
          label={_locate('ppp_atti_termine_giorni')}
          required
          value={form.termineConclusioneGiorni}
          onChange={(e) => setForm((f) => ({ ...f, termineConclusioneGiorni: e.target.value }))}
        />
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {_locate('ppp_atti_modalita_accesso')} *
          </label>
          <textarea
            rows={2}
            value={form.modalitaAccessoAtti}
            onChange={(e) => setForm((f) => ({ ...f, modalitaAccessoAtti: e.target.value }))}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <Input
          label={_locate('ppp_atti_sostitutiva_nome')}
          value={form.sostitutivaNome}
          onChange={(e) => setForm((f) => ({ ...f, sostitutivaNome: e.target.value }))}
        />
        <Input
          label={_locate('ppp_atti_sostitutiva_contatto')}
          value={form.sostitutivaContatto}
          onChange={(e) => setForm((f) => ({ ...f, sostitutivaContatto: e.target.value }))}
        />
      </div>
      <div className="flex justify-end">
        <LoadingButton
          isLoading={saving}
          customFunc={valid ? handleSubmit : undefined}
          text={_locate('ppp_atti_genera')}
        />
      </div>
    </div>
  );
}

function FormAvvioProcedimento({ procedimentoId, onSuccess, _locate }) {
  const api = usePppApi();
  const [saving, setSaving] = useState(false);
  const [tipo, setTipo] = useState('delibera');

  const handleSubmit = () => {
    setSaving(true);
    api.generaAvvioProcedimento(procedimentoId, { tipo }, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onFormError: () => setSaving(false),
    });
  };

  return (
    <div className="border border-amber-200 rounded-lg p-4 bg-amber-50 space-y-3">
      <p className="text-sm font-medium text-amber-800">{_locate('ppp_atti_avvio_titolo')}</p>
      <div className="flex items-center gap-3">
        <label className="block text-sm font-medium text-gray-700">{_locate('ppp_atti_tipo')}</label>
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
        >
          <option value="delibera">Delibera</option>
          <option value="determinazione">Determinazione</option>
        </select>
        <LoadingButton
          isLoading={saving}
          customFunc={handleSubmit}
          text={_locate('ppp_atti_genera')}
        />
      </div>
    </div>
  );
}

function FormApprovazioneDip({ procedimentoId, onSuccess, _locate }) {
  const api = usePppApi();
  const [saving, setSaving] = useState(false);
  const [dipId, setDipId] = useState('');
  const [tipo, setTipo] = useState('determinazione');

  const handleSubmit = () => {
    if (!dipId) return;
    setSaving(true);
    api.generaApprovazioneDip(procedimentoId, { dip_id: parseInt(dipId), tipo }, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onFormError: () => setSaving(false),
    });
  };

  return (
    <div className="border border-purple-200 rounded-lg p-4 bg-purple-50 space-y-3">
      <p className="text-sm font-medium text-purple-800">{_locate('ppp_atti_approvazione_dip_titolo')}</p>
      <div className="flex items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_atti_tipo')}</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
          >
            <option value="delibera">Delibera</option>
            <option value="determinazione">Determinazione</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_atti_dip_id')} *</label>
          <input
            type="number"
            min="1"
            value={dipId}
            onChange={(e) => setDipId(e.target.value)}
            placeholder="ID DIP"
            className="w-28 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <LoadingButton
          isLoading={saving}
          customFunc={dipId ? handleSubmit : undefined}
          text={_locate('ppp_atti_genera')}
        />
      </div>
    </div>
  );
}

function FormSelezioneProposte({ procedimentoId, onSuccess, _locate }) {
  const api = usePppApi();
  const [saving, setSaving] = useState(false);
  const [tipo, setTipo] = useState('determinazione');

  const handleSubmit = () => {
    setSaving(true);
    api.generaSelezioneProposte(procedimentoId, { tipo }, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onFormError: () => setSaving(false),
    });
  };

  return (
    <div className="border border-teal-200 rounded-lg p-4 bg-teal-50 space-y-3">
      <p className="text-sm font-medium text-teal-800">{_locate('ppp_atti_selezione_proposte_titolo')}</p>
      <div className="flex items-center gap-3">
        <label className="block text-sm font-medium text-gray-700">{_locate('ppp_atti_tipo')}</label>
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
        >
          <option value="determinazione">Determinazione</option>
          <option value="delibera">Delibera</option>
          <option value="provvedimento">Provvedimento</option>
        </select>
        <LoadingButton
          isLoading={saving}
          customFunc={handleSubmit}
          text={_locate('ppp_atti_genera')}
        />
      </div>
    </div>
  );
}

function FormRichiestaIntegrazioni({ procedimentoId, onSuccess, _locate }) {
  const api = usePppApi();
  const [saving, setSaving] = useState(false);
  const [richiestaId, setRichiestaId] = useState('');

  const handleSubmit = () => {
    if (!richiestaId) return;
    setSaving(true);
    api.generaRichiestaIntegrazioni(procedimentoId, { richiesta_integrazione_id: parseInt(richiestaId) }, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onFormError: () => setSaving(false),
    });
  };

  return (
    <div className="border border-orange-200 rounded-lg p-4 bg-orange-50 space-y-3">
      <p className="text-sm font-medium text-orange-800">{_locate('ppp_atti_richiesta_integrazioni_titolo')}</p>
      <div className="flex items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_atti_richiesta_id')} *</label>
          <input
            type="number"
            min="1"
            value={richiestaId}
            onChange={(e) => setRichiestaId(e.target.value)}
            placeholder="ID Richiesta"
            className="w-36 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <LoadingButton
          isLoading={saving}
          customFunc={richiestaId ? handleSubmit : undefined}
          text={_locate('ppp_atti_genera')}
        />
      </div>
    </div>
  );
}

export default function AttiTab({ procedimento, _locate, onProcedimentoChange }) {
  const api = usePppApi();
  const { request } = useHttp();
  const { can } = useAuthUser();
  const [atti, setAtti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generandoNomina, setGenerandoNomina] = useState(false);
  const [showForm, setShowForm] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchAtti = useCallback(() => {
    if (!procedimento?.id) return;
    setLoading(true);
    api.listAtti(procedimento.id, {
      onSuccess: (data) => { setAtti(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  }, [procedimento?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAtti(); }, [fetchAtti]);

  const handleGeneraNomina = () => {
    setGenerandoNomina(true);
    api.generaNominaRup(procedimento.id, {
      onSuccess: () => { setGenerandoNomina(false); fetchAtti(); },
      onFormError: () => setGenerandoNomina(false),
    });
  };

  const handleDownloadPdf = (atto) => {
    setDownloadingId(atto.id);
    request(api.scaricaPdfAtto(procedimento.id, atto.id), {
      method: 'GET',
      download: true,
      onSuccess: async (response) => {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `atto_${atto.tipo}_${atto.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        setDownloadingId(null);
      },
      onFormError: () => setDownloadingId(null),
    });
  };

  const handleInviaFirma = (atto) => {
    setActionLoadingId(atto.id);
    api.inviaFirmaAtto(procedimento.id, atto.id, {
      onSuccess: () => { setActionLoadingId(null); fetchAtti(); },
      onFormError: () => setActionLoadingId(null),
    });
  };

  const handleApprova = (atto) => {
    setActionLoadingId(atto.id);
    api.approvaAtto(procedimento.id, atto.id, {
      onSuccess: () => {
        setActionLoadingId(null);
        fetchAtti();
        if (atto.tipo === 'nomina_rup') onProcedimentoChange?.();
      },
      onFormError: () => setActionLoadingId(null),
    });
  };

  const handlePubblica = (atto) => {
    setActionLoadingId(atto.id);
    api.pubblicaAtto(procedimento.id, atto.id, {
      onSuccess: () => {
        setActionLoadingId(null);
        fetchAtti();
        if (atto.tipo === 'nomina_rup') onProcedimentoChange?.();
      },
      onFormError: () => setActionLoadingId(null),
    });
  };

  const toggleForm = (name) => setShowForm((v) => (v === name ? null : name));

  const buildAttoMenuItems = (atto) => {
    const btnClass = 'w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-100 text-left';
    const items = [
      <button key="pdf" onClick={() => handleDownloadPdf(atto)} className={btnClass}>
        {downloadingId === atto.id ? '...' : _locate('ppp_atti_scarica_pdf')}
      </button>,
    ];
    if (atto.stato === 'bozza' && can('ppp.atti.create')) {
      items.push(
        <button key="firma" onClick={() => handleInviaFirma(atto)} className={btnClass}>
          {_locate('ppp_atti_invia_firma')}
        </button>
      );
    }
    if (atto.stato === 'in_firma' && can('ppp.atti.approve')) {
      items.push(
        <button key="approva" onClick={() => handleApprova(atto)} className={btnClass}>
          {_locate('ppp_atti_approva')}
        </button>
      );
    }
    if (atto.stato === 'approvato' && can('ppp.atti.publish')) {
      items.push(
        <button key="pubblica" onClick={() => handlePubblica(atto)} className={btnClass}>
          {_locate('ppp_atti_pubblica')}
        </button>
      );
    }
    return items;
  };

  if (loading) {
    return <div className="p-4 text-gray-400 text-sm">{_locate('ppp_caricamento')}</div>;
  }

  return (
    <div className="p-4 space-y-6">
      {/* Genera atti precompilati */}
      {can('ppp.atti.create') && (
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-700">{_locate('ppp_atti_genera_titolo')}</p>
        <div className="flex flex-wrap gap-2">
          <LoadingButton
            isLoading={generandoNomina}
            customFunc={procedimento.rupId ? handleGeneraNomina : undefined}
            text={_locate('ppp_atti_genera_nomina_rup')}
          />
          <SimpleButton customFunc={() => toggleForm('avvio')} text={_locate('ppp_atti_genera_avvio')} />
          <SimpleButton customFunc={() => toggleForm('comunicazione')} text={_locate('ppp_atti_genera_comunicazione')} />
          <SimpleButton customFunc={() => toggleForm('dip')} text={_locate('ppp_atti_genera_approvazione_dip')} />
          <SimpleButton customFunc={() => toggleForm('selezione')} text={_locate('ppp_atti_genera_selezione_proposte')} />
          <SimpleButton customFunc={() => toggleForm('integrazioni')} text={_locate('ppp_atti_genera_richiesta_integrazioni')} />
        </div>
        {!procedimento.rupId && (
          <p className="text-xs text-amber-600">{_locate('ppp_atti_nomina_rup_warning')}</p>
        )}

        {showForm === 'avvio' && (
          <FormAvvioProcedimento
            procedimentoId={procedimento.id}
            onSuccess={() => { setShowForm(null); fetchAtti(); }}
            _locate={_locate}
          />
        )}
        {showForm === 'comunicazione' && (
          <FormComunicazioneAvvio
            procedimentoId={procedimento.id}
            onSuccess={() => { setShowForm(null); fetchAtti(); }}
            _locate={_locate}
          />
        )}
        {showForm === 'dip' && (
          <FormApprovazioneDip
            procedimentoId={procedimento.id}
            onSuccess={() => { setShowForm(null); fetchAtti(); }}
            _locate={_locate}
          />
        )}
        {showForm === 'selezione' && (
          <FormSelezioneProposte
            procedimentoId={procedimento.id}
            onSuccess={() => { setShowForm(null); fetchAtti(); }}
            _locate={_locate}
          />
        )}
        {showForm === 'integrazioni' && (
          <FormRichiestaIntegrazioni
            procedimentoId={procedimento.id}
            onSuccess={() => { setShowForm(null); fetchAtti(); }}
            _locate={_locate}
          />
        )}
      </div>
      )}

      {/* Lista atti */}
      {atti.length === 0 ? (
        <p className="text-sm text-gray-400">{_locate('ppp_atti_vuoti')}</p>
      ) : (
        <div className="space-y-2">
          {atti.map((atto) => (
            <div key={atto.id} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {TIPO_LABELS[atto.tipo] ?? atto.tipo}
                  {atto.numero && <span className="ml-1 text-gray-400">— N° {atto.numero}</span>}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{atto.oggetto}</p>
                {atto.dataEmissione && (
                  <p className="text-xs text-gray-400">
                    {_locate('ppp_atti_emesso_il')} {new Date(atto.dataEmissione).toLocaleDateString('it-IT')}
                    {atto.dataPubblicazione && (
                      <span className="ml-2">— {_locate('ppp_atti_pubblicato_il')} {new Date(atto.dataPubblicazione).toLocaleDateString('it-IT')}</span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <StatoAtto stato={atto.stato} />
                {actionLoadingId === atto.id ? (
                  <span className="text-xs text-gray-400">...</span>
                ) : (
                  <DropdownMenu menu_items={buildAttoMenuItems(atto)} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
